import {
	Company,
	Notification,
	User,
	currentSession,
	getNotifications,
	markNotificationAsRead,
	markAllNotificationsAsRead,
	ReportDesignCategoryEnum,
	getAllowedReportDesignCategoriesForUser,
} from "@q-food/q-food";
import { useStorage } from "@vueuse/core";
import { socketClient } from "~utils/websocket.ts";
import { utcDate } from "~utils/Date.ts";
import { error } from "@formkit/core";
import { i18n, loadTranslations } from "~/plugins/I18n.ts";

export const useUserStore = defineStore("User", () => {
	// TODO: Fetch user settings async
	const activeCompany = ref<Company | null>(null);
	const activeUser = ref<(User & { allowedReportDesignCategories: string[] }) | null>(null);
	const activeLanguageCode = ref<string>();
	const activePermissions = ref<string[]>([]);
	const userNotifications = ref<Notification[]>([]);
	const sessionKey = useStorage<string | null>("qfood-session-token", null);
	const authenticated = computed(() => sessionKey.value != null);
	// The token the websocket is currently connected with, so we can tell a user switch from a refresh.
	let socketToken: string | null = null;

	async function setToken(newSessionKey: string | null) {
		sessionKey.value = newSessionKey;

		if (newSessionKey == null) {
			activeUser.value = null;
			activeCompany.value = null;
			socketToken = null;
			await socketClient.deactivate();
		} else {
			await refreshUserInfo();
		}
	}

	async function refreshUserInfo() {
		const { user, company, permissions, notifications, languageCode } = await currentSession().catch((response) => {
			switch (response.status) {
				case 403:
					// no need to show message
					break;
				default:
					console.log(response);
					const errorMessage = response.message ?? "Er is iets misgegaan met het ophalen van de gebruikersgegevens";
					throw Error(errorMessage);
			}
		});

		activeCompany.value = company ?? null;
		activeUser.value = user ?? null;
		activePermissions.value = permissions;
		userNotifications.value = notifications;
		activeLanguageCode.value = languageCode ?? "nl";

		if (
			i18n.global.locale.value != activeLanguageCode.value ||
			!i18n.global.availableLocales.includes(activeLanguageCode.value)
		) {
			await loadTranslations(activeLanguageCode.value).catch((e) => console.log(e));
		}

		if (company != null && company.settings.primaryThemeColor != null) {
			const root = document.documentElement;
			root.style.setProperty("--primary-theme-color", company.settings.primaryThemeColor);
		}

		if (activeUser.value !== null) {
			const token = localStorage.getItem("qfood-session-token")!;
			socketClient.connectHeaders = { "x-auth-token": token };

			// activate() does nothing on an already-active client, so after logging in as someone else the
			// socket would keep the previous user's token and keep receiving their /user/channel messages.
			// Only reconnect when the token actually changed — refreshUserInfo also runs on every
			// refreshApp (including ones triggered by websocket messages), which must not cycle the socket.
			if (socketClient.active && socketToken !== token) {
				await socketClient.deactivate();
			}
			socketToken = token;
			socketClient.activate();

			await getAllowedReportDesignCategoriesForUser()
				.then((response) => {
					activeUser.value.allowedReportDesignCategories = response;
				})
				.catch((err) => {
					console.log(err);
					const errorMessage = err.message ?? "Er is iets misgegaan met het ophalen van de toegestane categorieën";
					error(errorMessage);
				});
		}
	}

	async function refreshNotifications() {
		const n = await getNotifications();
		userNotifications.value = n;
	}

	async function readNotification(notification: Notification) {
		const notif = userNotifications.value.find((it) => it.id == notification.id);
		notif.readAt = utcDate().format();

		await markNotificationAsRead(notification.id);
	}

	async function readAllNotifications() {
		for (const notif of userNotifications.value) {
			notif.readAt = utcDate().format();
		}

		await markAllNotificationsAsRead();
	}

	function isAllowed(privilege: string) {
		if (activeUser.value == null) {
			return false;
		}

		return activePermissions.value.includes(privilege) || activeUser.value.isAdmin;
	}

	function isReportCategoryAllowed(category: string) {
		if (activeUser.value == null) {
			return false;
		}

		if (activeUser.value.isAdmin) {
			return true;
		}

		return activeUser.value.allowedReportDesignCategories?.includes(category) ?? false;
	}

	function getPrinterId(category: ReportDesignCategoryEnum | null): string | undefined {
		const preference =
			activeUser.value?.settings.printerPreferences.find((it) => it.category == category) ??
			activeUser.value?.settings.printerPreferences.find((it) => it.category == null);
		return preference?.printerId;
	}

	return {
		activeCompany: computed(() => {
			if (!activeCompany.value) {
				throw new Error("No active company");
			}

			return activeCompany.value;
		}),
		activeOwner: computed(() => {
			const owner = activeCompany.value?.defaultOwner;

			if (owner == null) {
				throw new Error("No default owner for active company!");
			}

			return owner;
		}),
		activeUser: readonly(activeUser),
		activeLanguageCode: readonly(activeLanguageCode),
		automaticRecalculation: computed(() => {
			if (activeUser.value == null) return [];

			return Array.of(...activeUser.value.settings.automaticRecalculation.values());
		}),
		smartTabs: computed(() => {
			if (activeUser.value == null) return [];

			return Array.of(...activeUser.value.settings.smartTabs.values());
		}),
		authenticated: readonly(authenticated),
		permissions: readonly(activePermissions),
		notifications: readonly(userNotifications),
		sessionKey: readonly(sessionKey),
		isAllowed,
		isReportCategoryAllowed,
		setToken,
		readNotification,
		readAllNotifications,
		refreshNotifications,
		refreshUserInfo,
		getPrinterId,
	};
});
